import { useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/hooks/useTenant";
import { CheckedInTrailerCombobox } from "@/components/orders/CheckedInTrailerCombobox";
import { assertTrailerAssignable } from "@/lib/trailer-assignability";

export interface TrailerNeedsServiceBlock {
  trailerId: string;
  reason: string | null;
}

interface TrailerIdFieldProps {
  value: string;
  /** Optional baseline used to skip the needs-service check when the value is
   *  unchanged (e.g. edit-mode hydration). Defaults to `value`. */
  baselineValue?: string;
  /** Fired only after the needs-service check passes (or when clearing). */
  onChange: (value: string) => void;
  /** Called when the picked trailer is flagged Needs Service. Parent shows
   *  the shared `NeedsServiceBlockedDialog` — the field itself does not
   *  render UI for this. */
  onNeedsServiceBlock: (info: TrailerNeedsServiceBlock) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Reusable Trailer ID picker for order Create/Edit and per-leg pickers.
 * Wraps `CheckedInTrailerCombobox` with the shared Needs-Service assignability
 * gate so every entry point (order-level + per-leg) enforces the same rule
 * and surfaces the same list of checked-in trailers.
 */
export function TrailerIdField({
  value,
  baselineValue,
  onChange,
  onNeedsServiceBlock,
  id,
  placeholder,
  disabled,
}: TrailerIdFieldProps) {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const baseline = (baselineValue ?? value).trim();

  return (
    <CheckedInTrailerCombobox
      id={id}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={async (next) => {
        const trimmed = next.trim();
        // Only run the guard when the user is switching to a NEW non-empty
        // trailer. Clearing or reselecting the same trailer is always safe.
        if (trimmed && trimmed !== baseline) {
          const res = await assertTrailerAssignable(trimmed, tenantId ?? null, queryClient);
          if (res.ok === false) {
            onNeedsServiceBlock({ trailerId: res.trailerId, reason: res.serviceReason ?? null });
            return;
          }
        }
        onChange(next);
      }}
    />
  );
}